import { motion } from "framer-motion";
import Timeline from "@/components/Timeline";
import { CheckCircle2 } from "lucide-react";

const LOCATION_IMG =
  "https://images.unsplash.com/photo-1776090188612-a2dab458ce14?auto=format&fit=crop&w=1400&q=80";
const PORTRAIT_IMG =
  "https://images.unsplash.com/photo-1776090188651-a1ec2cf2bdb0?auto=format&fit=crop&w=800&q=80";

const pageVariants = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.5 } },
  exit: { opacity: 0, transition: { duration: 0.3 } },
};

const scheune = [
  "220 qm Eventhalle",
  "Bis zu 100 Personen",
  "Theke mit Spüle",
  "Bestuhlung & Tische inklusive",
  "Bühnenaufbau möglich",
  "Hohe Decken & Charakter",
];
const aussen = [
  "Großzügiges Außengelände",
  "Grillmöglichkeit",
  "Terrasse",
  "Bühnenaufbau möglich",
  "Open-Air-Events",
  "Parkplätze vorhanden",
];

export default function Location() {
  return (
    <motion.main
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      data-testid="location-page"
    >
      {/* Page Hero */}
      <div className="relative h-[55vh] min-h-[420px] overflow-hidden flex items-end">
        <img
          src={LOCATION_IMG}
          alt="Atelier 77 – Eventhalle"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#141414]/40 to-[#141414]" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-14 w-full">
          <p
            className="text-[#C1440E] text-xs tracking-[0.4em] uppercase mb-3"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Die Location
          </p>
          <h1
            className="text-5xl md:text-7xl font-extrabold uppercase text-[#F2EDE8] leading-none"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            data-testid="location-title"
          >
            Atelier 77
          </h1>
          <p
            className="text-[#D4A843] italic text-xl mt-2"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Künstlerhof & Eventlocation in Bochum-Laer
          </p>
        </div>
      </div>

      {/* Timeline */}
      <section className="py-24 md:py-32 px-6 md:px-12 max-w-7xl mx-auto" data-testid="timeline-section">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <p
            className="text-[#C1440E] text-xs tracking-[0.4em] uppercase mb-4"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Geschichte
          </p>
          <h2
            className="text-4xl md:text-5xl font-light text-[#F2EDE8]"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            302 Jahre in vier Kapiteln
          </h2>
        </motion.div>
        <Timeline />
      </section>

      {/* Klaus Teichmann */}
      <section className="py-24 bg-[#1C1C1E]" data-testid="teichmann-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative overflow-hidden max-w-md"
              style={{ aspectRatio: "1" }}
            >
              <img
                src={PORTRAIT_IMG}
                alt="Atelier 77 – Industrieloft"
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-[#141414]/90">
                <p
                  className="text-[#D4A843] font-semibold text-sm"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  Klaus Teichmann
                </p>
                <p
                  className="text-[#A19D98] text-xs"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  Künstler & Inhaber · geb. 1964 in Herne
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex flex-col gap-6"
            >
              <p
                className="text-[#C1440E] text-xs tracking-[0.4em] uppercase"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Der Künstler
              </p>
              <h2
                className="text-4xl md:text-5xl font-light text-[#F2EDE8]"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                Klaus Teichmann
              </h2>
              <p
                className="text-[#A19D98] leading-relaxed text-sm"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Geb. 1964 in Herne, lebt und arbeitet in Bochum. Klaus Teichmann ist Bildender
                Künstler und Inhaber des Atelier 77. Er hat mit Pharmaunternehmen, Museen und Zoos,
                Medienanstalten sowie Film- und Computerspielproduktionen zusammengearbeitet.
              </p>
              <p
                className="text-[#A19D98] leading-relaxed text-sm"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Seine Vision für das Atelier 77 ist simpel und zugleich außergewöhnlich: Ein Raum
                für Freidenker und frei schaffende Künstler – der gleichzeitig der perfekte Ort
                für unvergessliche Feiern ist.
              </p>
              <blockquote
                className="border-l-2 border-[#C1440E] pl-6 text-[#D4A843] italic text-lg"
                style={{ fontFamily: "'Cormorant Garamond', serif" }}
              >
                "Kunst und Feierlichkeit sind keine Gegensätze – sie ergänzen sich."
              </blockquote>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Räumlichkeiten */}
      <section className="py-24 md:py-32 px-6 md:px-12 max-w-7xl mx-auto" data-testid="spaces-section">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <p
            className="text-[#C1440E] text-xs tracking-[0.4em] uppercase mb-4"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            Räumlichkeiten
          </p>
          <h2
            className="text-4xl md:text-5xl font-light text-[#F2EDE8]"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Ausstattung & Details
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6">
          {[
            { title: "Partyscheune", badge: "Innen", desc: "Ca. 220 qm Eventhalle mit hohen Decken und rustikalem Charme. Platz für bis zu 100 Personen, ausgestattet mit Theke, Spüle, Bestuhlung und Tischen.", items: scheune, id: "scheune" },
            { title: "Außengelände", badge: "Außen", desc: "Das weitläufige Außengelände bietet vielfältige Möglichkeiten: Grillen, Terrasse oder ein Bühnenaufbau für Open-Air-Veranstaltungen.", items: aussen, id: "aussengelaende" },
          ].map((space, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: i * 0.15 }}
              className="bg-[#1C1C1E] p-8 border border-[#F2EDE8]/5"
              data-testid={`${space.id}-card`}
            >
              <p
                className="text-[#D4A843] text-xs tracking-widest uppercase mb-3"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                {space.badge}
              </p>
              <h3
                className="text-2xl md:text-3xl font-extrabold uppercase text-[#F2EDE8] mb-4"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                {space.title}
              </h3>
              <p
                className="text-[#A19D98] text-sm leading-relaxed mb-6"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                {space.desc}
              </p>
              <div className="space-y-2.5">
                {space.items.map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle2 size={13} className="text-[#C1440E] flex-shrink-0" />
                    <span
                      className="text-[#F2EDE8]/65 text-sm"
                      style={{ fontFamily: "'Outfit', sans-serif" }}
                    >
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </section>
    </motion.main>
  );
}
